/* eslint-disable max-lines-per-function */
import { ArbeitsbereichOption } from '@/domain/mitarbeiter/features/rotationsplan/entities/ArbeitsbereichOption';
import { ArbeitsbereichSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/ArbeitsbereichSelect';
import { StandortSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateStation/components/StandortSelect';
import { DauerEinheitSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/CreateVorlageStation/components/DauerEinheitSelect';
import {
  LERNZIELE_MAX_LENGTH,
  type useForm as useCreateForm,
} from '@/domain/mitarbeiter/features/rotationsplan/features/CreateVorlageStation/useForm';
import { AbteilungsleiterSelect } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/AbteilungsleiterSelect/AbteilungsleiterSelect';
import { ErinnerungsZeitenMultiSelectV2 } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/ErinnerungsZeitenMultiSelectV2';
import { UnsavedChangesDialog } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/components/UnsavedChangesDialog';
import { VORLAGE_STATION_FORM_ID } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/constants';
import type { RotationsplanVorlageDetails } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplanVorlageById/useRotationsplanVorlageById';
import { FieldBase } from '@/domain/mitarbeiter/shared/components/form/fieldComponents/FieldBase';
import { Mitarbeiter_RotationsplanVorlageErinnerungsZeit } from '@/gql/graphql';
import {
  Body,
  Button,
  InputCounter,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Switch,
} from '@e2n/ui/v2';
import { Suspense, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Formular-Sheet einer Station. Das `form` kommt vom Anlegen oder Bearbeiten.
 */
export function VorlageStationSheet({
  titel,
  vorlage,
  form,
  close,
}: {
  titel: string;
  vorlage: RotationsplanVorlageDetails;
  form: ReturnType<typeof useCreateForm>;
  close: () => void;
}) {
  const { t } = useTranslation();
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);

  const hasUnsavedChanges = () =>
    Object.values(form.state.fieldMeta).some((field) => !field?.isDefaultValue);

  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => {
          if (open) {
            return;
          }

          if (hasUnsavedChanges()) {
            setShowUnsavedChangesDialog(true);
          } else {
            close();
          }
        }}>
        <SheetContent
          aria-describedby={undefined}
          className="data-[side=right]:w-full sm:data-[side=right]:max-w-130">
          <SheetHeader>
            <SheetTitle className="flex-1">{titel}</SheetTitle>
            <Button
              className="md:hidden"
              disabled={form.state.isSubmitting}
              form={VORLAGE_STATION_FORM_ID}>
              {t('actions.fertig')}
            </Button>
          </SheetHeader>
          <SheetBody className="flex grow flex-col gap-4 overflow-auto sm:p-6">
            <Body size="sm" className="text-labels-secondary">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.hinweisPflichtfelder',
              )}
            </Body>
            <form
              id={VORLAGE_STATION_FORM_ID}
              className="flex flex-col gap-4"
              onSubmit={(event) => {
                event.preventDefault();
                form.handleSubmit();
              }}>
              <form.AppField
                name="standort"
                listeners={{
                  onChange: () =>
                    form.setFieldValue(
                      'arbeitsbereich',
                      undefined as unknown as ArbeitsbereichOption,
                    ),
                }}>
                {(field) => (
                  <FieldBase
                    isRequired
                    label={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.standort.label',
                    )}>
                    <Suspense fallback={<Skeleton className="h-10 w-full" />}>
                      <StandortSelect
                        handleChange={field.handleChange}
                        value={field.state.value}
                        land={vorlage.land}
                      />
                    </Suspense>
                  </FieldBase>
                )}
              </form.AppField>
              <form.Subscribe selector={(state) => state.values.standort?.id}>
                {(standortId) => (
                  <form.AppField
                    name="arbeitsbereich"
                    listeners={{
                      onChange: () =>
                        form.setFieldValue('abteilungsleiter', undefined),
                    }}>
                    {(field) => (
                      <FieldBase
                        isRequired
                        label={t(
                          'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.arbeitsbereich.label',
                        )}>
                        <Suspense
                          fallback={<Skeleton className="h-10 w-full" />}>
                          <ArbeitsbereichSelect
                            disabled={!standortId}
                            value={field.state.value}
                            standortId={standortId ?? ''}
                            handleChange={field.handleChange}
                          />
                        </Suspense>
                      </FieldBase>
                    )}
                  </form.AppField>
                )}
              </form.Subscribe>
              <form.AppField name="dauer">
                {(field) => (
                  <FieldBase
                    isRequired
                    label={t(
                      'domain.mitarbeiter.rotationsplan.vorlagen.createStation.fields.dauer.label',
                    )}>
                    <div className="flex gap-2">
                      <InputCounter
                        id={field.name}
                        className="w-30 shrink-0"
                        min={1}
                        aria-invalid={!field.state.meta.isValid}
                        value={field.state.value}
                        onChange={field.handleChange}
                      />
                      <form.AppField name="dauerEinheit">
                        {(einheitField) => (
                          <div className="min-w-0 flex-1">
                            <DauerEinheitSelect
                              value={einheitField.state.value}
                              handleChange={einheitField.handleChange}
                            />
                          </div>
                        )}
                      </form.AppField>
                    </div>
                  </FieldBase>
                )}
              </form.AppField>
              <form.AppField name="lernziele">
                {(field) => (
                  <field.TextAreaInput
                    label={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.lernziele.label',
                    )}
                    showCounter
                    maxLength={LERNZIELE_MAX_LENGTH}
                  />
                )}
              </form.AppField>
              <form.AppField name="abteilungsleiter">
                {(field) => (
                  <FieldBase
                    label={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.label',
                    )}
                    infoText={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.abteilungsleitung.tooltip',
                    )}>
                    <Suspense fallback={<Skeleton className="h-10 w-full" />}>
                      <form.Subscribe
                        selector={(state) => state.values.arbeitsbereich?.id}>
                        {(selectedArbeitsbereichId) => (
                          <AbteilungsleiterSelect
                            selectedArbeitsbereichId={selectedArbeitsbereichId}
                            onChange={field.handleChange}
                            selectedAbteilungsleiter={field.state.value}
                          />
                        )}
                      </form.Subscribe>
                    </Suspense>
                  </FieldBase>
                )}
              </form.AppField>
              <form.AppField
                name="fristErinnerung"
                listeners={{
                  onChange: ({ value }) => {
                    if (!value) {
                      form.setFieldValue('erinnerungszeiten', []);
                    }
                  },
                }}>
                {(field) => (
                  <Switch
                    id={field.name}
                    checked={field.state.value}
                    onCheckedChange={field.handleChange}
                    label={t(
                      'domain.mitarbeiter.rotationsplan.rotationsplaene.createStation.fields.fristErinnerung',
                    )}
                  />
                )}
              </form.AppField>
              <form.Subscribe
                selector={(state) => state.values.fristErinnerung}>
                {(fristErinnerung) =>
                  fristErinnerung && (
                    <form.AppField name="erinnerungszeiten">
                      {(field) => (
                        <FieldBase
                          isRequired
                          label={t(
                            'domain.mitarbeiter.fristen.erinnerungsZeit.label',
                          )}>
                          <ErinnerungsZeitenMultiSelectV2
                            values={field.state.value}
                            handleChange={(value) =>
                              field.handleChange(
                                value as Mitarbeiter_RotationsplanVorlageErinnerungsZeit[],
                              )
                            }
                          />
                        </FieldBase>
                      )}
                    </form.AppField>
                  )
                }
              </form.Subscribe>
            </form>
          </SheetBody>
          <SheetFooter className="hidden md:flex">
            <Button
              type="submit"
              disabled={form.state.isSubmitting}
              form={VORLAGE_STATION_FORM_ID}>
              {t('actions.fertig')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <UnsavedChangesDialog
        open={showUnsavedChangesDialog}
        onCancel={() => setShowUnsavedChangesDialog(false)}
        onConfirm={() => {
          form.reset();
          setShowUnsavedChangesDialog(false);
          close();
        }}
      />
    </>
  );
}
